import React, {useEffect, useRef, useState} from 'react';
import IndexingRow from './IndexingRow';
import IndexOverview from './IndexOverview';
import {ReactComponent as PlusIcon} from '../../../assests/svg/plus-icon.svg';
import {ReactComponent as ExchangeIcon} from '../../../assests/svg/icon_shift.svg';
import {ReactComponent as GlowRight} from '../../../assests/svg/glow-right.svg';
import {useDispatch, useSelector} from 'react-redux';
import {getIndex} from '../../../api/EcaServices';
import {RootState} from '../../../redux/store';
import {Typography, Button} from '@mui/material';
import {t} from 'i18next';
import {setIndexingBannerData} from '../../../redux/slices/indexingManagementSlice';
import IndexingBanner from './IndexingBanner';
import {
  resetCreateCustomIndex,
  setAddCustomIndex,
  setCreateFieldTemplateId,
  setIndexingList,
  setShowToast,
} from '../../../redux/slices/indexingSlice';
import {useToast} from '../../../contexts/toastContext/ToastContext';
import {useNavigate} from 'react-router-dom';
import {indexingStatusKey} from '../model';
import AurToast from '../../common/aur-toast/AurToast';
import AurButton from '../../common/aur-button/AurButton';
import {setModalStatus} from '../../../redux/slices/invoiceModalSlice';
import {
  AurDropdownButton,
  DropdownIconTypes,
} from '../../common/aur-button/AurDropdownButton';
import AurIcon, {IconColors, IconNames} from '../../common/aur-icon/AurIcon';
import ProjectSwitcherModal from '../../ecapages/eca-overview/eca-overlay/ProjectSwitcherModal';
import AurLoadingIndicator from '../../common/aur-loading-indicator/AurLoadingIndictor';
import {
  PAGE_TYPE,
  useManageMenuByPermissions,
} from '../../../hooks/permissions/useManageMenuByPermissions';
import {SecurePage} from '../../pages/SecurePage';
import {
  PAGE_NAME,
  usePermissionsByPage,
} from '../../../hooks/permissions/usePermissionsByPage';
import AurDataGridNoRowsOverlay from '../../common/aur-data-grid-no-rows-overlay/AurDataGridNoRowsOverlay';
import {useAnalytics} from '../../../hooks/useAnalytics';
import {
  GA_CLICK_TYPES,
  GA_EVENT_NAMES,
  GA_REGIONS,
} from '../../../constants/analyticsConstants';

const IndexManagementLanding = () => {
  const {trackGAEvent} = useAnalytics();
  const dispatch = useDispatch();
  const clientId = useSelector(
    (state: RootState) => state.selectedClient
  ).clientId;
  const {selectedProjectName, projectId} = useSelector(
    (state: RootState) => state.reports
  );
  const selectedProject = useSelector(
    (state: RootState) => state.invoiceModal.modalDropDownSelectedItem
  );

  const isFetchingIndexRef = useRef(false);
  const [reqLoading, setReqLoading] = useState<boolean>(false);
  const [ecaIndexData, setEcaIndexData] = useState<any>([]);
  const [openCreateCustomPopup, setOpenCreateCustomPopup] =
    useState<boolean>(false);

  const {manageMenus} = useManageMenuByPermissions(PAGE_TYPE.Index);
  const {hasECIIndexingPerm} = usePermissionsByPage(PAGE_NAME.INDEX_MANAGEMENT);
  const {showToast} = useToast();
  const showToastMsg = useSelector(
    (state: RootState) => state.indexes.showToast
  );
  const showToastMsgType = useSelector(
    (state: RootState) => state.indexes.showToastType
  );

  const isCreatedCustomIndex = useSelector(
    (state: RootState) => state.indexes.addCustomIndex
  );

  const indexCustom = useSelector(
    (state: RootState) => state.indexes.indexCustom || {}
  );

  // Fetch index data from API
  const fetchIndex = async () => {
    isFetchingIndexRef.current = true;
    setReqLoading(true);
    setEcaIndexData([]);
    try {
      const params = `?clientId=${clientId}&projectId=${projectId}`;
      const result = await getIndex(params);

      const foundObject = result.data.data.find(
        (item: any) => item.type === 'Standard'
      );
      setEcaIndexData(result.data.data);
      dispatch(setIndexingList(result.data.data));
      if (foundObject) {
        dispatch(setCreateFieldTemplateId(foundObject.fieldTemplateId));
      }
      if (isCreatedCustomIndex) {
        setOpenCreateCustomPopup(true);
        setTimeout(() => {
          setOpenCreateCustomPopup(false);
        }, 5000);
        dispatch(setAddCustomIndex(false));
      }
    } catch (e) {
      setEcaIndexData([]);
      console.error('Error', e);
    } finally {
      setReqLoading(false);
      isFetchingIndexRef.current = false;
    }
  };

  // Function to check the status of in-progress indexes
  const checkInProgressStatus = (
    data: {
      type: string;
      status: string;
    }[]
  ) => {
    if (!data || !Array.isArray(data)) {
      return t('common.noDataAvailable');
    }
    // Filter items with the status "Indexing Event(s) In Progress"
    const inProgressItems = data.filter(
      (item) => item.status === indexingStatusKey.inProgress
    );
    const inProgressCount = inProgressItems.length;

    // Prepare the appropriate message based on the count of "Indexing Event(s) In Progress" statuses
    let message = '';
    if (inProgressCount > 1) {
      message = t('indexes.multipleIndexBannerTitle');
    } else if (inProgressCount === 1) {
      const indexType = inProgressItems[0]?.type;

      message = indexType && `${indexType} ${t('indexes.indexBannerTitle')}`;
    }

    // Dispatch the data with the correct message and status
    dispatch(
      setIndexingBannerData({
        message,
        showIndexBanner: inProgressCount > 0,
      })
    );

    // Return the result based on the in-progress count
    if (inProgressCount > 1) {
      return t('indexing.moreThanOneProgress');
    }
    if (inProgressCount === 1) {
      return t('indexing.oneInProgress');
    }
    return '0';
  };

  useEffect(() => {
    if (!hasECIIndexingPerm) return;
    if (isFetchingIndexRef.current) return;

    if (projectId && clientId) fetchIndex();
  }, [projectId, clientId, hasECIIndexingPerm]);

  useEffect(() => {
    dispatch(setModalStatus(false));
  }, [dispatch]);

  useEffect(() => {
    if (ecaIndexData && Array.isArray(ecaIndexData)) {
      checkInProgressStatus(ecaIndexData);
    }
  }, [ecaIndexData]); // This effect runs whenever ecaIndexData changes

  const indexing = useSelector(
    (state: RootState) => state.indexesBanner.indexBannerData
  );

  useEffect(() => {
    if (showToastMsg) {
      if (showToastMsgType === 'Success') {
        showToast(t('indexes.customIndex.indexBuildStart'), 'success');
      } else {
        showToast(t('indexes.customIndex.failedAddingDocs'), 'error');
      }

      setInterval(() => {
        dispatch(setShowToast(false));
      }, 3000);
    }
  }, []);
  const navigate = useNavigate();
  const navigateToCreateCustomIndex = () => {
    trackGAEvent({
      eventName: GA_EVENT_NAMES.CLICK,
      click: {
        name: t('gaTracking.indexManagement.newCustomIndex'),
        type: GA_CLICK_TYPES.BUTTON,
        region: GA_REGIONS.BODY,
        action: t('gaTracking.indexManagement.newIndexBegin'),
      },
    });
    dispatch(resetCreateCustomIndex());
    navigate('/indexmanagement/create-custom-index');
  };
  const projectButtonHandler = () => {
    dispatch(setModalStatus(true));
  };
  const handleSnackbarClose = () => {
    setOpenCreateCustomPopup(false);
  };

  const renderContent = () => {
    if (
      reqLoading ||
      hasECIIndexingPerm === undefined ||
      isFetchingIndexRef?.current
    ) {
      return <AurLoadingIndicator overlay isOpen />;
    }

    if (!hasECIIndexingPerm) {
      return <SecurePage />;
    }

    if (ecaIndexData?.length > 0) {
      return <IndexingRow ecaIndexData={ecaIndexData} />;
    }

    return <AurDataGridNoRowsOverlay />;
  };

  return (
    <>
      <div className="index-banner-container">
        {indexing.showIndexBanner ? (
          <IndexingBanner message={indexing.message} />
        ) : null}
      </div>
      <GlowRight
        className="index-glow-effect-landing-page"
        aria-hidden="true"
      />
      <div className="parent-container">
        <IndexOverview />
        <div className="indexing-header">
          <div className="header">
            <div className="header-title-container">
              <Typography className="typo-text subhead3_size light-blue">
                {selectedProjectName}
              </Typography>
              <Typography
                className="typo-text display3_size light-blue"
                component="h1"
              >
                {t('ecaIndexing.indexmanagement')}
              </Typography>
            </div>
            <div
              style={{display: 'flex', alignItems: 'flex-end'}}
              className="index-header-action"
            >
              {hasECIIndexingPerm && (
                <Button
                  id="basic-button"
                  data-testid="new-custom-index"
                  aria-label={t('ecaIndexing.newcustomindex')}
                  startIcon={<PlusIcon aria-hidden="true" />}
                  className="typo-text buttonMedium_size custom-menu-button custom-button add-custom-index"
                  variant="contained"
                  sx={{
                    textTransform: 'none',
                  }}
                  onClick={navigateToCreateCustomIndex}
                >
                  {t('ecaIndexing.newcustomindex')}
                </Button>
              )}
              {manageMenus?.length > 0 && (
                <AurDropdownButton
                  buttonText={t('eca.ecaHomepage.manage')}
                  hintText={t('eca.ecaHomepage.manage')}
                  type="continue"
                  aria-live="polite"
                  size="Medium"
                  iconType="Arrow"
                  iconSize="medium"
                  iconClassName="client-profile-details-dropdown-icon"
                  menuDirectionLeft={false}
                  menuItemHeight="medium"
                  menuItemWidth="mediumLarge"
                  menuItemTextSize="small"
                  dropDownIconType={DropdownIconTypes.Chev}
                  menuItems={manageMenus}
                  classNames="index-manage-dropdown"
                >
                  <AurIcon
                    icon={IconNames.ChevronDown}
                    color={IconColors.Custom}
                    customClassName="client-profile-details-dropdown-icon"
                    aria-hidden="true"
                  />
                </AurDropdownButton>
              )}
              <AurButton
                ariaLabel={t('accessibility.indexManagement.projectSwitcher')}
                ariaDescription={t(
                  'accessibility.indexManagement.projectSwitcherHint'
                )}
                classNames="typo-text buttonMedium_size project-switcher"
                onClick={projectButtonHandler}
                type="continue"
                size="Medium"
                startIcon={<ExchangeIcon aria-hidden />}
                dataTestId="open-project-workspace-dialog"
              >
                {t('reports.inTakeAuditTab.header.projectButton')}
              </AurButton>
            </div>
          </div>
          {hasECIIndexingPerm && (
            <Typography
              className="typo-text subhead3_size light-blue"
              style={{marginTop: '32px'}}
            >
              {t('ecaIndexing.totalindexes')} ({ecaIndexData?.length || '0'})
            </Typography>
          )}
        </div>
        {renderContent()}
        <ProjectSwitcherModal
          initialSelectedProjectId={projectId ?? selectedProject.id}
        />
        <AurToast
          ariaLabel={t('common.toastAlert.toastAlertHint')}
          hideDuration={5000}
          open={openCreateCustomPopup}
          horizontalPosition={'center'}
          message={t(`indexes.configureIndex.confirmToastMessage`, {
            name: indexCustom.indexName,
          })}
          onClose={() => handleSnackbarClose}
        />
      </div>
    </>
  );
};

export default IndexManagementLanding;
